import React from 'react';
import './MultiSelectToggleDropdown.scss';
import { ReactComponent as ChevronDown } from '../../images/chevron-down.svg';
import { ReactComponent as CloseIcon } from '../../images/close-x-icon.svg';

export interface MultiSelectToggleDropdownOption {
  label: string;
  value: string;
}

interface MultiSelectToggleDropdownProps {
  options: MultiSelectToggleDropdownOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export const MultiSelectToggleDropdown: React.FC<MultiSelectToggleDropdownProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Select options',
}) => {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleRemoveFirst = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== selected[0]));
  };

  return (
    <div className={`multi-toggle-dropdown${open ? ' multi-toggle-dropdown--open' : ''}`} ref={dropdownRef}>
      <div className='multi-toggle-dropdown__container'>
        <div className='multi-toggle-dropdown__input'>
          <div className='multi-toggle-dropdown__input-wrapper' tabIndex={0} onClick={() => setOpen((o) => !o)}>
            <div className='multi-toggle-dropdown__content'>
              {selected.length === 0 ? (
                <span className='multi-toggle-dropdown__text'>{placeholder}</span>
              ) : (
                <>
                  <span className='chip'>
                    {options.find((o) => o.value === selected[0])?.label || selected[0]}
                    <span className='chip-close' onClick={handleRemoveFirst}>
                      <CloseIcon />
                    </span>
                  </span>
                  {selected.length > 1 && <span className='count-chip'>+{selected.length - 1}</span>}
                </>
              )}
            </div>
            <span className='multi-toggle-dropdown__chevron'>
              <ChevronDown />
            </span>
          </div>
        </div>
        {open && (
          <div className='multi-toggle-dropdown__dropdown-list'>
            <div className='multi-toggle-dropdown__dropdown-list-title'>Card overlay</div>
            <div className='multi-toggle-dropdown__dropdown-list-divider' />
            {options.map((opt) => (
              <div
                key={opt.value}
                className='multi-toggle-dropdown__dropdown-list-item'
                tabIndex={0}
                onClick={() => handleToggle(opt.value)}
              >
                <div
                  className={`toggle-switch${selected.includes(opt.value) ? ' active' : ''}`}
                  tabIndex={0}
                  role='button'
                  aria-pressed={selected.includes(opt.value)}
                >
                  <div className='switch-handle' />
                </div>
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
